import axios from "axios";
import { API } from "../App";
import { adminOrgBase, adminWorkspaceIdBase, adminOrgRoute, matchAdminPortalRoute } from "../config/adminPortalRoutes";

export const adminPortalRolloutMode = (organization) =>
  ["legacy", "opt_in", "new_default"].includes(organization?.admin_portal_rollout)
    ? organization.admin_portal_rollout
    : "legacy";

const ALLOWED_TASK_PARAMS = {
  status: new Set(["active", "issues", "pending"]),
  tab: new Set(["calendar", "locations", "meetings"]),
  type: new Set(["board", "coach", "household", "parent", "player", "staff"]),
  view: new Set(["enrollments"]),
};

const ALLOWED_FINANCE_PARAMS = {
  view: new Set([
    "billing", "collections", "discounts", "payouts", "receivables",
    "registrations", "transactions",
  ]),
};

const taskSearch = (pathname, search = "") => {
  const source = new URLSearchParams(String(search).replace(/^\?/, ""));
  const accepted = new URLSearchParams();
  const allowed = /(?:\/finance|\/admin\/financials)$/.test(pathname)
    ? ALLOWED_FINANCE_PARAMS
    : ALLOWED_TASK_PARAMS;
  Object.entries(allowed).forEach(([key, values]) => {
    const value = source.get(key);
    if (values.has(value)) accepted.set(key, value);
  });
  const query = accepted.toString();
  return query ? `?${query}` : "";
};

const withTaskSearch = (pathname, search) => `${pathname}${taskSearch(pathname, search)}`;

const legacyDestination = (pathname) => {
  const exact = {
    "/admin": "today", "/admin/programs": "programs", "/admin/programs/new": "programs/new",
    "/admin/people": "people", "/admin/households": "people/households",
    "/admin/registrations": "people/enrollments", "/admin/staff": "people/directory",
    "/admin/team": "people/directory", "/admin/coaches": "people/coaches", "/admin/compliance": "people/directory",
    "/admin/meetings": "operations/scheduling", "/admin/scheduling": "operations/scheduling", "/admin/facilities": "operations/facilities",
    "/admin/financials": "finance", "/admin/org-invoices": "finance", "/admin/invoices": "finance/invoices",
    "/admin/payment-plans": "finance/payment-plans", "/admin/promo-codes": "finance/promo-codes",
    "/admin/messaging": "communications", "/admin/social": "communications/social", "/admin/support": "support",
    "/admin/marketplace": "marketplace", "/admin/website": "website", "/admin/profile": "profile",
    "/admin/wstl": "extensions/wstl", "/admin/modules/tournaments": "extensions/tournaments",
    "/admin/modules/leagues": "extensions/leagues", "/admin/developer": "extensions/developer",
    "/admin/settings": "settings", "/admin/settings/import": "settings/import", "/admin/import": "settings",
  };
  if (exact[pathname]) return exact[pathname];
  if (/^\/admin\/programs\/(new\/[A-Za-z0-9_-]+|[A-Za-z0-9_-]+(\/edit)?)$/.test(pathname)) return `programs/${pathname.slice("/admin/programs/".length)}`;
  if (/^\/admin\/households\/[A-Za-z0-9_-]+$/.test(pathname)) return `people/households/${pathname.slice("/admin/households/".length)}`;
  if (/^\/admin\/coaches\/[A-Za-z0-9_-]+$/.test(pathname)) return "people/directory";
  if (/^\/admin\/reports(\/[A-Za-z0-9_-]+)*$/.test(pathname)) return `finance/reports${pathname.slice("/admin/reports".length)}`;
  if (/^\/admin\/connect(\/(connections|review|history|rules))?$/.test(pathname)) return `connect${pathname.slice("/admin/connect".length)}`;
  return "not-found";
};

const legacyDefaultSearch = (pathname) => {
  if (pathname === "/admin/import") return "?tab=import";
  if (pathname === "/admin/org-invoices") return "?view=receivables";
  if (pathname === "/admin/registrations") return "?view=enrollments";
  if (pathname === "/admin/meetings") return "?tab=meetings";
  if (pathname === "/admin/staff" || pathname === "/admin/team") return "?type=staff";
  if (/^\/admin\/coaches\/[A-Za-z0-9_-]+$/.test(pathname)) return "?type=coach";
  if (pathname === "/admin/compliance") return "?status=issues";
  return "";
};

export const legacyToAdminPortalPath = (pathname, orgSlug, search = "", workspaceOrgId) => {
  const path = String(pathname).replace(/\/$/, "");
  const destination = legacyDestination(path);
  const candidate = `${workspaceOrgId !== undefined ? adminWorkspaceIdBase(workspaceOrgId) : adminOrgBase(orgSlug)}/${destination}`;
  const route = matchAdminPortalRoute(candidate, orgSlug, "organization", workspaceOrgId) ? candidate : adminOrgRoute(orgSlug, "not-found", workspaceOrgId);
  const supplied = new URLSearchParams(search);
  const params = new URLSearchParams(legacyDefaultSearch(path));
  for (const key of new Set(supplied.keys())) params.delete(key);
  supplied.forEach((value, key) => params.append(key, value));
  params.delete("legacy");
  // Authentication credentials are never forwarded as workspace query state.
  params.delete("token");
  return `${route}${params.size ? `?${params}` : ""}`;
};

export const legacyToAdminPortalUrl = (pathname, orgSlug, search = "", mainAppUrl) => {
  const origin = new URL(mainAppUrl);
  if (!["http:", "https:"].includes(origin.protocol)) {
    throw new Error("Main app URL must use HTTP or HTTPS");
  }
  return new URL(
    legacyToAdminPortalPath(pathname, orgSlug, search),
    `${origin.origin}/`,
  ).toString();
};

export const adminPortalToLegacyPath = (pathname, orgSlug, search = "") => {
  const prefix = `/org/${orgSlug}/`;
  const suffix = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : "today";
  if (suffix.startsWith("programs/new/") || /^programs\/[A-Za-z0-9_-]+(\/edit)?$/.test(suffix)) return withTaskSearch(`/admin/programs/${suffix.slice("programs/".length)}`, search);
  if (/^people\/households\/[A-Za-z0-9_-]+$/.test(suffix)) return withTaskSearch(`/admin/households/${suffix.slice("people/households/".length)}`, search);
  if (suffix === "people/households") return withTaskSearch("/admin/households", search);
  if (suffix === "people/enrollments") return withTaskSearch("/admin/registrations", search);
  if (suffix === "people/directory") return withTaskSearch("/admin/people", search);
  if (suffix === "programs") return "/admin/programs";
  if (suffix === "people") return withTaskSearch("/admin/people", search);
  if (suffix === "operations/scheduling") return withTaskSearch("/admin/scheduling", search);
  if (suffix === "operations/facilities") return "/admin/facilities";
  if (suffix === "finance") return withTaskSearch("/admin/financials", search);
  if (suffix === "finance/invoices") return "/admin/invoices";
  if (suffix === "finance/payment-plans") return "/admin/payment-plans";
  if (suffix === "finance/promo-codes") return "/admin/promo-codes";
  if (suffix.startsWith("finance/reports")) return `/admin/reports${suffix.slice("finance/reports".length)}`;
  if (suffix === "communications") return "/admin/messaging";
  if (suffix === "communications/social") return "/admin/social";
  if (suffix === "support") return "/admin/support";
  if (suffix === "marketplace") return "/admin/marketplace";
  if (suffix === "settings/import") return "/admin/settings/import";
  if (suffix === "settings") return "/admin/settings";
  return "/admin";
};

const privacySafeRoute = (pathname) => {
  const segments = String(pathname || "/").split("/").filter(Boolean);
  if (segments[0] === "org") {
    const section = segments[2] || "today";
    const subsection = ["operations", "finance", "communications"].includes(section) ? segments[3] : null;
    return `/org/:org/${[section, subsection].filter(Boolean).join("/")}`;
  }
  if (segments[0] === "admin") return `/admin${segments[1] ? `/${segments[1]}` : ""}`;
  return "/admin";
};

export const trackAdminPortalEvent = (event) => {
  try {
    return Promise.resolve(axios.post(`${API}/organizations/admin-portal/events`, {
      ...event,
      route: privacySafeRoute(event.route),
    })).catch(() => undefined);
  } catch {
    return undefined;
  }
};

export const trackAdminWorkflowCompleted = (workflow) => {
  const route = window.location.pathname;
  trackAdminPortalEvent({
    event_type: "workflow_completed",
    shell: route.startsWith("/org/") ? "organization" : "legacy",
    route,
    workflow,
  });
};
